import { dehydrate, hydrate, type QueryClient } from "@tanstack/react-query";

/**
 * Guarda no navegador só as listas públicas (feed, discos recentes, vitrines)
 * para que voltar ao app mostre conteúdo na hora e atualize em segundo plano.
 */
const STORAGE_KEY = "viny:qc:v1";
const MAX_AGE = 24 * 60 * 60_000;
const PERSISTED_PREFIXES = new Set([
  "community-feed",
  "following-feed",
  "spotlight-feed",
  "recent-collection-feed-public",
  "feed-side-recent",
  "community-faces",
  "collector-social-proof",
  "community-artists",
]);

export function persistQueryCache(queryClient: QueryClient): () => void {
  if (typeof window === "undefined") return () => {};

  // Espera a página terminar de montar antes de restaurar, para não conflitar
  // com o HTML vindo do servidor.
  let restored = false;
  const restore = () => {
  if (restored) return;
  restored = true;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (raw) {
      const saved = JSON.parse(raw) as { at: number; state: unknown };
      if (Date.now() - saved.at < MAX_AGE) {
        hydrate(queryClient, saved.state as never);
      } else {
        window.localStorage.removeItem(STORAGE_KEY);
      }
    }
  } catch {
    window.localStorage.removeItem(STORAGE_KEY);
  }
  };
  const restoreTimer = window.setTimeout(() => requestAnimationFrame(restore), 0);

  let timer: number | undefined;
  const save = () => {
    timer = undefined;
    try {
      const state = dehydrate(queryClient, {
        shouldDehydrateQuery: (q) =>
          q.state.status === "success" &&
          PERSISTED_PREFIXES.has(String(q.queryKey[0])),
      });
      window.localStorage.setItem(STORAGE_KEY, JSON.stringify({ at: Date.now(), state }));
    } catch {
      /* armazenamento cheio ou indisponível: segue sem cache */
    }
  };

  const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || timer !== undefined) return;
    timer = window.setTimeout(() => {
      const idle = (window as { requestIdleCallback?: (cb: () => void) => void }).requestIdleCallback;
      idle ? idle(save) : save();
    }, 1500);
  });

  return () => {
    unsubscribe();
    window.clearTimeout(restoreTimer);
    if (timer !== undefined) window.clearTimeout(timer);
  };
}

export function clearPersistedQueryCache() {
  try {
    window.localStorage.removeItem(STORAGE_KEY);
  } catch {
    /* noop */
  }
}
